import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { Input, Grid, Table, TableBody, TableCell, TableRow, TableFooter, TablePagination } from "@material-ui/core";
import React, { Component, Fragment } from "react";

import EnhancedTableHead from "components/Global/Table/EnhancedTableHead";
import PropTypes from "prop-types";
import { Search as SearchIcon } from "@material-ui/icons";
import _ from "lodash";
import { compose } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import shortid from "shortid";
import CircularProgress from '@material-ui/core/CircularProgress';
import TablePaginationHelper from "../../../utils/tablePaginationHelper";

const DESC_ORDER = "desc";
const ASC_ORDER = "asc";

const SearchInput = styled(Input)`
  width: 240px;
  margin-bottom: 10px;
`;

const EmptyListDiv = styled.div`
  && {
    margin-top: 17px,
    margin-bottom: 17px
  }
`;

const SelectedNoItemGrid = styled(Grid)`
  &&&& {
    margin: 4px;
    padding: 12px;
    background-color: rgb(242, 242, 242);
    font-family: "Roboto Medium", "Roboto Regular", Roboto;
    font-weight: 500;
    font-style: normal;
    font-size: 17px;
    color: rgb(153, 153, 153);
  }
`;

const alternateTheme = createMuiTheme({
  palette: {
    primary: {
      main: "#2196f3"
    }
  }
});

export class EnhancedTable extends Component {
  static get DESC_ORDER() {
    return DESC_ORDER;
  }
  static get ASC_ORDER() {
    return ASC_ORDER;
  }

  tablePaginationHelper = new TablePaginationHelper(this.props.tableConfigItemId, 5, this.props.instanceId || "default");

  constructor(props) {
    super(props);
    this.state = {
      order: props.order,
      orderBy: props.orderBy,
      rows: props.rows,
      filterText: undefined,
      displayedRowsAreDirty: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (
      (state.displayedRows === undefined && props.rows !== undefined) ||
      state.rows !== props.rows ||
      state.displayedRowsAreDirty
    ) {
      let rows = props.rows || [];
      if (state.filterText) {
        rows = EnhancedTable.filterRows(state.filterText, rows, props.columnDefinitions);
      }

      if (state.orderBy) {
        rows = EnhancedTable.sortRows(state.order, state.orderBy, rows)
      }

      return {
        displayedRows: rows,
        displayedRowsAreDirty: false
      };
    }
    return null;
  }

  static filterRows = (filterText, rows, columnDefinitions) => {
    return rows.filter(row =>
      columnDefinitions.some(columnDefinition => {
        const cellValue = columnDefinition.cellValueCalculator
          ? columnDefinition.cellValueCalculator(row)
          : _.get(row, columnDefinition.dataField);
          if(!columnDefinition.isNotFilterable)
            return (
              cellValue !== undefined &&
              cellValue
                .toString()
                .toUpperCase()
                .includes(filterText.toUpperCase())
            );
      })
    );
  }

  static sortRows = (direction, columnDefinition, rows) => {
    const {
      isString,
      isDateField,
      dataField,
      sortOverride,
      cellValueCalculator
    } = columnDefinition;
    const sortedRows = rows.sort((a, b) => {
      let dataFieldA = {};
      let dataFieldB = {};

      if (sortOverride) {
        dataFieldA = _.get(a, sortOverride);
        dataFieldB = _.get(b, sortOverride);
      } else if (cellValueCalculator) {
        dataFieldA = cellValueCalculator(a);
        dataFieldB = cellValueCalculator(b);
      } else {
        dataFieldA = _.get(a, dataField);
        dataFieldB = _.get(b, dataField);
      }
      if (isString) {
        const aValue = dataFieldA ? dataFieldA.toLowerCase() : "";
        const bValue = dataFieldB ? dataFieldB.toLowerCase() : "";
        return (
          (direction === DESC_ORDER ? -1 : 1) * aValue.localeCompare(bValue)
        );
      } else if (isDateField) {
        // for dates
        const aValue = dataFieldA ? moment(dataFieldA) : moment();
        const bValue = dataFieldB ? moment(dataFieldB) : moment();

        // based on direction
        if (aValue.isBefore(bValue)) {
          return direction === DESC_ORDER ? -1 : 1;
        } else if (aValue.isAfter(bValue)) {
          return direction === DESC_ORDER ? 1 : -1;
        }

        // strings must be equal
        return 0;
      }
      // number comparison, or if dataType is not known
      const aValue = dataFieldA || 0;
      const bValue = dataFieldB || 0;

      return direction === ASC_ORDER ? aValue - bValue : bValue - aValue;
    });

    return sortedRows;
  };

  handleSortGrid = (event, columnDefinition) => {
    if (this.props.sortable) {
      const newOrder =
        this.state.orderBy === columnDefinition &&
        this.state.order !== DESC_ORDER
          ? DESC_ORDER
          : ASC_ORDER;
      const newOrderBy = columnDefinition;

      this.setState({
        order: newOrder,
        orderBy: newOrderBy,
        displayedRowsAreDirty: true
      });
    }
  };

  renderSearch(){
    return this.props.useGridLayout ?
    this.props.searchable ? (
      <Grid container direction="row" justify="flex-end" alignItems="flex-end" spacing={0} style={{marginBottom:"10px"}}>
        {this.props.headerComponent && <Grid item xs={10}>
          {this.props.headerComponent}
        </Grid>}
        <Grid item xs={this.props.headerComponent ? 2 : 4} style={{whiteSpace: "nowrap"}}>
          <MuiThemeProvider theme={alternateTheme} >
              <SearchInput
                type="text"
                id="searchName"
                name="searchName"
                placeholder={this.props.searchHintText}
                autoComplete="off"
                onChange={eventArgs => this.setState({ filterText: eventArgs.target.value, displayedRowsAreDirty: true })}
              />
            </MuiThemeProvider>
            <SearchIcon color="primary" />
        </Grid>
      </Grid>
    ) : null
    :
    this.props.searchable ? (
      <Fragment>
        <MuiThemeProvider theme={alternateTheme}>
        <SearchInput
            type="text"
            id="searchName"
            name="searchName"
            placeholder={this.props.searchHintText}
            autoComplete="off"
            onChange={eventArgs => this.setState({ filterText: eventArgs.target.value, displayedRowsAreDirty: true })}
          />
        </MuiThemeProvider>
        <SearchIcon color="primary" />
        <br />
      </Fragment>
    ) : null;
  }

  renderCellValue = (columnDefinition, row) => {
    if (columnDefinition.cellComponent) {
      return columnDefinition.cellComponent(row);
    }
    if (columnDefinition.cellValueCalculator) {
      return columnDefinition.cellValueCalculator(row);
    }
    if (columnDefinition.secondaryDataField) {
      return `${_.get(row, columnDefinition.dataField)} ${_.get(
        row,
        columnDefinition.secondaryDataField
      )}`;
    }
    return _.get(row, columnDefinition.dataField);
  };

  //TablePagination
  handleChangePage = (event, newPage) => {
    if (!event) {
      return null;
    }    
    this.tablePaginationHelper.page = newPage;
  };

  handleChangeRowsPerPage = event => {
    this.tablePaginationHelper.rowsPerPage = event.target.value;
    this.forceUpdate();
  };

  renderTablePagination = () => {
    if(!this.props.pageable) return null;
    return (<TableFooter>
      <TableRow>
        <TablePagination
          rowsPerPage={this.tablePaginationHelper.rowsPerPage}
          tableConfigItemId={this.props.tableConfigItemId}
          count={this.state.displayedRows.length}
          rowsPerPageOptions={[5, 10, 25, 100]}
          page={this.tablePaginationHelper.page}
          backIconButtonProps={{"aria-label": "Previous Page"}}
          nextIconButtonProps={{"aria-label": "Next Page"}}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
          labelDisplayedRows={() => `${this.tablePaginationHelper.page + 1} of ${Math.ceil(this.state.displayedRows.length / this.tablePaginationHelper.rowsPerPage)}`}
        />
      </TableRow>
    </TableFooter>)
  }
  //TablePagination

  render() {
    const displayedRows = this.props.pageable ?
                            this.state.displayedRows.slice(((this.tablePaginationHelper.page) * this.tablePaginationHelper.rowsPerPage), ((this.tablePaginationHelper.page+1) * this.tablePaginationHelper.rowsPerPage))
                            :
                            this.state.displayedRows;

    const showLoadingIndicator = this.props.showLoadingIndicator;
    return (
      <Fragment>
        {this.props.rows && this.props.rows.length > 0 && this.renderSearch()}
        {(!displayedRows ||
          displayedRows.length === 0) && (
            <EmptyListDiv>
              <Grid
                container
                direction="row"
                justify="flex-start"
                alignItems="center"
                spacing={8}
                style={{ paddingBottom: "8px" }}
              >
                {showLoadingIndicator ? 
                  <div style={{width:"100%", textAlign:"center", marginTop: "30px", fontSize: "16pt"}}>
                    <CircularProgress />
                  </div>
                  :
                  <SelectedNoItemGrid container item>
                    {this.props.rows && this.props.rows.length > 0
                      ? this.props.noSearchResultsFoundMessage
                      : this.props.noRowsMessage}
                  </SelectedNoItemGrid>
                }                
              </Grid>
            </EmptyListDiv>
          )}
        {displayedRows && displayedRows.length > 0 && (
          <div
            style={{
              height: this.props.height,
              minHeight: this.props.minHeight,
              maxHeight: this.props.maxHeight,
              width: this.props.width,
              minWidth: this.props.minWidth,
              maxWidth: this.props.maxWidth,
              overflowY: this.props.overflowY || "auto"
            }}
          >
            <Table
              className="loansList_Table"
              style={{
                height: this.props.height,
                minHeight: this.props.minHeight,
                maxHeight: this.props.maxHeight,
                width: this.props.width,
                minWidth: this.props.minWidth,
                maxWidth: this.props.maxWidth
              }}
            >
              <EnhancedTableHead
                sortable={this.props.sortable}
                order={this.state.order}
                orderBy={this.state.orderBy === null ? null : this.state.orderBy.id}
                onRequestSort={this.handleSortGrid}
                columns={this.props.columnDefinitions}
                shouldHideConfig={this.props.shouldHideConfig}
                shouldHideExport={this.props.shouldHideExport}
                stickyHeader={this.props.stickyHeader}
              />
              <TableBody>
                {displayedRows.map(row => (
                  <TableRow key={shortid.generate()}>
                    {this.props.columnDefinitions.map(columnDefinition => {
                      if (columnDefinition.cell !== undefined) {
                        return (
                          <columnDefinition.cell
                            key={columnDefinition.id}
                            data={row}
                            disabled={this.props.disabled}
                          />
                        );
                      }
                      return (
                        <TableCell  key={columnDefinition.id} align={columnDefinition.align}>
                          {this.renderCellValue(columnDefinition, row)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
              {this.renderTablePagination()}
            </Table>
          </div>
        )}
      </Fragment>
    );
  }
}

EnhancedTable.propTypes = {
  sortable: PropTypes.bool,
  columnDefinitions: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  order: PropTypes.string,
  orderBy: PropTypes.object,
  shouldHideConfig: PropTypes.bool,
  shouldHideExport: PropTypes.bool,
  searchHintText: PropTypes.string,
  searchable: PropTypes.bool,
  noSearchResultsFoundMessage: PropTypes.string,
  noRowsMessage: PropTypes.string,
  height: PropTypes.number,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  width: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
  stickyHeader: PropTypes.bool,
  disabled: PropTypes.bool,
  tableConfigItemId: PropTypes.string.isRequired,
  instanceId: PropTypes.string.isRequired
};

EnhancedTable.defaultProps = {
  sortable: true,
  order: EnhancedTable.ASC_ORDER,
  orderBy: null,
  shouldHideConfig: false,
  shouldHideExport: false,
  searchHintText: "Search",
  searchable: false,
  noSearchResultsFoundMessage: "No rows match the search criteria",
  noRowsMessage: "No rows found",
  height: undefined,
  minHeight: undefined,
  maxHeight: undefined,
  width: undefined,
  minWidth: undefined,
  maxWidth: undefined,
  stickyHeader: false,
  disabled: false
};

function mapStateToProps() {
  return {};
}

export default compose(
  connect(
    mapStateToProps,
    {}
  )
)(EnhancedTable);
